// ─── Unit conversion for stored history ───
// Weights, distances and tape measurements are stored in whatever unit was active when
// they were entered (lbs / mi / in, or kg / km / cm). Switching units therefore has to
// convert the stored numbers, not just the labels.
//
// Rounding can never make lbs → kg → lbs exact in both directions, so every converted
// record keeps its pre-conversion value in an `orig` field ({units, ...fields}). Toggling
// back restores those values exactly and drops `orig`; entries created after the switch
// simply convert by rounding. Records are rebuilt without `orig` when the user edits
// them, which is correct: the edited number is native to the current unit.
//
// This module is pure (no imports) so the reducer can use it and it can be tested in node.

const LB_PER_KG = 2.2046226218487757;
const KM_PER_MI = 1.609344;
const CM_PER_IN = 2.54;

// Multiply by FACTORS[dimension][fromUnit] to get the value in the other unit system
const FACTORS = {
  weight:   { lbs: 1 / LB_PER_KG, kg: LB_PER_KG },
  distance: { lbs: KM_PER_MI,     kg: 1 / KM_PER_MI },  // mi ↔ km
  length:   { lbs: CM_PER_IN,     kg: 1 / CM_PER_IN },  // in ↔ cm
};

const isUnit = (u) => u === "lbs" || u === "kg";
const num = (v) => { const n = typeof v === "number" ? v : parseFloat(v); return Number.isFinite(n) ? n : null; };
const decimalsOf = (step) => (String(step).split(".")[1] || "").length;
const fix = (v, step) => Number((Math.round(v / step) * step).toFixed(decimalsOf(step)));
const stepFor = (kind) => (kind === "distance" ? 0.01 : 0.1);
const has = (v) => v !== undefined && v !== null && v !== "";

/**
 * Convert one value by rounding. `kind` is "lift" | "bodyWeight" | "distance" | "length".
 * Empty, non-numeric and zero values are returned untouched; strings stay strings so
 * form drafts convert as cleanly as saved records.
 */
export function convertValue(v, kind, from, to) {
  if (from === to || !isUnit(from) || !isUnit(to)) return v;
  const n = num(v);
  if (n === null || n === 0) return v;
  const dim = kind === "distance" ? "distance" : kind === "length" ? "length" : "weight";
  const out = fix(n * FACTORS[dim][from], stepFor(kind));
  return typeof v === "string" ? String(out) : out;
}

export const BODY_LENGTH_KEYS = ["waist", "hips", "chest", "neck", "arms", "thighs", "calves", "shoulders", "forearms"];

// Convert the listed fields of a record, restoring exact pre-conversion values when the
// record carries an `orig` snapshot taken in the target unit.
function convertRecord(rec, fields, from, to) {
  if (from === to || !rec || typeof rec !== "object") return rec;
  const out = { ...rec };
  const restore = rec.orig && rec.orig.units === to ? rec.orig : null;
  const snapshot = {};
  let changed = false;
  for (const [key, kind] of fields) {
    if (!has(rec[key])) continue;
    if (restore && has(restore[key])) { out[key] = restore[key]; changed = true; continue; }
    const v = convertValue(rec[key], kind, from, to);
    if (v !== rec[key]) { snapshot[key] = rec[key]; out[key] = v; changed = true; }
  }
  if (!changed) return rec;
  if (restore) delete out.orig;
  else if (Object.keys(snapshot).length) out.orig = { units: from, ...snapshot };
  return out;
}

// Works for saved workouts and for in-progress drafts (same shape, string values)
export function convertWorkout(w, from, to, exercises) {
  if (from === to || !w || !Array.isArray(w.exercises)) return w;
  const cardioIds = new Set((exercises || []).filter((e) => e && e.cat === "Cardio").map((e) => e.id));
  return {
    ...w,
    exercises: w.exercises.map((ex) => {
      if (!ex || !Array.isArray(ex.sets)) return ex;
      const fields = cardioIds.has(ex.exerciseId) ? [["distance", "distance"]] : [["weight", "lift"]];
      return { ...ex, sets: ex.sets.map((st) => convertRecord(st, fields, from, to)) };
    }),
  };
}

const BODY_FIELDS = [["weight", "bodyWeight"], ...BODY_LENGTH_KEYS.map((k) => [k, "length"])];
export function convertBodyEntry(b, from, to) {
  return convertRecord(b, BODY_FIELDS, from, to);
}

export function convertGoals(g, from, to) {
  return convertRecord(g, [["goalWeight", "bodyWeight"]], from, to);
}

/** Returns the converted slices of app state ({workouts, body, goals}); empty object if nothing to do. */
export function convertStateUnits(state, from, to) {
  if (from === to || !isUnit(from) || !isUnit(to) || !state) return {};
  const exs = state.exercises || [];
  return {
    workouts: (state.workouts || []).map((w) => convertWorkout(w, from, to, exs)),
    body: (state.body || []).map((b) => convertBodyEntry(b, from, to)),
    goals: convertGoals(state.goals || {}, from, to),
  };
}
